import {FormEvent, KeyboardEvent, useCallback, useEffect, useRef} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {useTranslation} from '@/translations/i18n.tsx'
import {QUERY_KEYS} from '@/queryClient.ts'
import {Button} from '@components/ui/button'
import {TextArea} from '@components/commons/textarea'
import {ArrowUpIcon, RefreshCcw02Icon, XCloseIcon} from '@components/ui/icon'
import {MessageBubble} from '@features/ai-chatbot/components/message-bubble'
import {useAssignment} from '@features/assignment/queries/useAssignment'
import {useChatStream} from '@features/ai-chatbot/hooks/use-chat-stream'
import {useInitChatSession} from '@features/ai-chatbot/queries/use-init-chat-session'
import {useChatStore} from '@features/ai-chatbot/store/store.ts'
import {InteractionState, Suggestion} from '@features/ai-chatbot/types.ts'
import {SUGGESTION_ONLY_STATES} from '@features/ai-chatbot/constants.ts'
import {cn} from '@utilities/helpers'

const SUGGESTION_BUTTON_CLASSNAME = cn(
    'flex w-full min-h-10 cursor-pointer items-start gap-2 border-b border-grey-200 py-2.5 text-left text-sm text-grey-1000 transition',
    '[&_svg]:shrink-0 [&_svg]:fill-grey-1000',
    'disabled:cursor-not-allowed disabled:opacity-50'
)

export const AiChatBody = ({onClose}: {onClose: () => void}) => {
    const {t} = useTranslation()
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)

    const {data: sessionData, isLoading: isInitializing, error: initError} = useInitChatSession()

    // Scoped to the assignment the session was created with, never to the one
    // the user has since navigated to: the backend was told about the former
    // only. On an assignment page the detail view has already fetched it, so
    // the listing title comes straight from the cache.
    const sessionAssignmentCode = sessionData?.assignmentCode
    const {data: assignment} = useAssignment(sessionAssignmentCode ? Number(sessionAssignmentCode) : undefined, {
        enabled: !!sessionAssignmentCode
    })
    const listingTitle = assignment?.entity.title

    const sessionId = sessionData?.data.sessionId
    const quoteTitle = sessionData?.data.currentQuote?.title

    // Greet with whatever context the assistant already has: the quote carried by
    // the init response, or failing that the listing of the assignment the
    // session was opened on. Only outside both does the generic message apply.
    const getWelcomeMessage = () => {
        if (quoteTitle) {
            return t('ai_chatbot:generic_root_flow:generic_start_message_with_quote', {quoteTitle})
        }
        if (listingTitle) {
            return t('ai_chatbot:generic_root_flow:generic_start_message_with_assignment', {listingTitle})
        }
        return t('ai_chatbot:generic_root_flow:generic_start_message')
    }
    const welcomeMessage = getWelcomeMessage()

    // The starter questions come from the init response only — there is no
    // static fallback, so an empty list just means no buttons are rendered.
    const starterQuestions = sessionData?.data.suggestedQuestions ?? []

    const messages = useChatStore(state => state.messages)
    const isStreaming = useChatStore(state => state.isStreaming)
    const currentStreamContent = useChatStore(state => state.currentStreamContent)
    const interactionState = useChatStore(state => state.interactionState)
    const suggestions = useChatStore(state => state.suggestions)
    const resetChat = useChatStore(state => state.reset)

    const queryClient = useQueryClient()
    const {sendMessage} = useChatStream(sessionId)

    const handleStartNewConversation = useCallback(() => {
        resetChat()
        queryClient.invalidateQueries({queryKey: [QUERY_KEYS.CHATBOT_SESSION]})
    }, [resetChat, queryClient])

    const isLoading = isStreaming || isInitializing
    const isReady = !!sessionId && !isInitializing

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({top: scrollRef.current.scrollHeight, behavior: 'smooth'})
        }
    }, [messages, currentStreamContent, isLoading])

    const handleQuickReply = useCallback(
        (suggestion: Suggestion) => {
            sendMessage(buildRequestFromSuggestion(suggestion, interactionState.type))
        },
        [sendMessage, interactionState.type]
    )

    const handleStarterQuestion = useCallback(
        (text: string) => {
            if (!isLoading && isReady) {
                sendMessage(buildRequestFromText(text, interactionState.type))
            }
        },
        [isLoading, isReady, sendMessage, interactionState.type]
    )

    const handleSubmitMessage = useCallback(
        (e: FormEvent<HTMLFormElement> | KeyboardEvent<HTMLTextAreaElement>) => {
            e.preventDefault()
            const input = inputRef.current?.value.trim()
            if (input && !isLoading && isReady) {
                sendMessage(buildRequestFromText(input, interactionState.type))
                if (inputRef.current) {
                    inputRef.current.value = ''
                }
            }
        },
        [isLoading, isReady, sendMessage, interactionState.type]
    )

    const onKeyDown = useCallback(
        (e: KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                handleSubmitMessage(e)
            }
        },
        [handleSubmitMessage]
    )

    const displayMessages = messages
        .map((msg, idx) => {
            const isLast = idx === messages.length - 1
            if (isLast && isStreaming && msg.role === 'assistant') {
                return {...msg, content: currentStreamContent}
            }
            return msg
        })
        .filter(msg => msg.role === 'user' || msg.content || msg.isError)

    const showLoadingBubble = isStreaming && !currentStreamContent
    const isInputDisabled = isLoading || !isReady || SUGGESTION_ONLY_STATES.has(interactionState.type)

    useEffect(() => {
        if (!isInputDisabled) {
            inputRef.current?.focus()
        }
    }, [isInputDisabled])

    return (
        <>
            <div className="bg-linear-(--gradient-rausch) flex items-center justify-between gap-2 px-4 py-3 text-white">
                <h2 className="text-sm font-medium">{t('ai_chatbot:header')}</h2>
                <div className="flex items-center gap-1">
                    <Button
                        aria-label={t('ai_chatbot:start_new_conversation')}
                        onClick={handleStartNewConversation}
                        shape="square"
                        type="button"
                        variant="ghostWhite"
                    >
                        <RefreshCcw02Icon />
                    </Button>
                    <Button
                        aria-label={t('commons:close')}
                        onClick={onClose}
                        shape="square"
                        type="button"
                        variant="ghostWhite"
                    >
                        <XCloseIcon />
                    </Button>
                </div>
            </div>

            <div
                aria-atomic="false"
                aria-live="polite"
                className="flex-1 overflow-y-auto p-4"
                ref={scrollRef}
                role="log"
            >
                {isInitializing && <TypingIndicator />}

                {!isInitializing && !!initError && (
                    <MessageBubble
                        message={{
                            id: 'init-error',
                            role: 'assistant',
                            content: '',
                            timestamp: new Date(),
                            isError: true
                        }}
                    />
                )}
                {!isInitializing && !initError && (
                    <MessageBubble
                        message={{
                            id: 'welcome',
                            role: 'assistant',
                            content: starterQuestions.length
                                ? `${welcomeMessage} ${t('ai_chatbot:starter_questions:intro')}`
                                : welcomeMessage,
                            timestamp: new Date()
                        }}
                    />
                )}

                {isReady && !isLoading && displayMessages.length === 0 && starterQuestions.length > 0 && (
                    <div className="flex flex-col">
                        {starterQuestions.map(question => (
                            <button
                                className={SUGGESTION_BUTTON_CLASSNAME}
                                key={question}
                                onClick={() => handleStarterQuestion(question)}
                                type="button"
                            >
                                <ArrowUpIcon size={20} />
                                {question}
                            </button>
                        ))}
                    </div>
                )}

                {displayMessages.map(message => (
                    <MessageBubble key={message.id} message={message} />
                ))}

                {!isLoading && suggestions.length > 0 && (
                    <div className="flex flex-col">
                        {suggestions.map(suggestion => (
                            <button
                                className={SUGGESTION_BUTTON_CLASSNAME}
                                key={String(suggestion.value)}
                                onClick={() => handleQuickReply(suggestion)}
                                type="button"
                            >
                                <ArrowUpIcon size={20} />
                                {suggestion.label}
                            </button>
                        ))}
                    </div>
                )}

                {showLoadingBubble && <TypingIndicator />}

                {interactionState.type === 'conversation_ended' && (
                    <div className="mt-4 flex flex-col items-center gap-3">
                        <p className="text-center text-sm text-grey-600">{t('ai_chatbot:conversation_ended')}</p>
                        <Button onClick={handleStartNewConversation} type="button" variant="secondary">
                            {t('ai_chatbot:start_new_conversation')}
                        </Button>
                    </div>
                )}
            </div>

            <form className="relative border-t border-grey-200 p-3" onSubmit={handleSubmitMessage}>
                {/* field-sizing-content grows the textarea with its content, capped by
                    max-h-50; the shared TextArea's default min height is overridden so
                    the composer doesn't reserve the label/help-text row. */}
                <TextArea
                    autoFocus
                    className={cn(
                        'size-full max-h-50 min-h-18 resize-none [&_div]:size-full [&_textarea]:field-sizing-content',
                        // The shared field uses a neutral border plus a rausch hover ring;
                        // Elevate's composer is a plain grey box that darkens on hover.
                        '[&>div>div]:border-grey-600 [&>div>div]:hover:border-grey-1000 [&>div>div]:hover:shadow-none [&>div>div]:hover:ring-0'
                    )}
                    disabled={isInputDisabled}
                    onKeyDown={onKeyDown}
                    placeholder={t('ai_chatbot:textarea_placeholder')}
                    ref={inputRef}
                    rows={1}
                />
                {/* Elevate's send button: circular and Airbnb near-black rather than the
                    rausch primary this design system defaults to. */}
                <Button
                    aria-label={t('ai_chatbot:send')}
                    className={cn(
                        'absolute right-4 bottom-4 rounded-full bg-grey-1000 text-white shadow-none',
                        'not-data-disabled:hover:bg-black not-data-disabled:hover:text-white active:bg-black',
                        'data-disabled:bg-grey-200 data-disabled:text-grey-500'
                    )}
                    disabled={isInputDisabled}
                    shape="square"
                    size="sm"
                    type="submit"
                >
                    <ArrowUpIcon />
                </Button>
            </form>
        </>
    )
}

AiChatBody.displayName = 'AiChatBody'

const TypingIndicator = () => {
    const {t} = useTranslation()
    return (
        <output aria-label={t('ai_chatbot:header')} className="mb-3 flex justify-start">
            <div className="flex gap-1 rounded-2xl rounded-bl-xs bg-grey-200 px-4 py-3">
                <span className="size-2 animate-bounce rounded-full bg-grey-500 [animation-delay:-0.3s]" />
                <span className="size-2 animate-bounce rounded-full bg-grey-500 [animation-delay:-0.15s]" />
                <span className="size-2 animate-bounce rounded-full bg-grey-500" />
            </div>
        </output>
    )
}

const buildRequestFromSuggestion = (
    suggestion: Suggestion,
    stateType: InteractionState['type']
): {
    text: string
    value?: string | number
    confirmAction?: boolean
    anythingElse?: boolean
    wantsToComment?: boolean
} => {
    const text = suggestion.label

    switch (stateType) {
        case 'param_required':
            return {text, value: suggestion.value}
        case 'confirmation_required':
            return {text, confirmAction: suggestion.value === 'Yes'}
        case 'anything_else_required':
            return {text, anythingElse: suggestion.value === 'Yes'}
        case 'rating_required':
            return {text, value: suggestion.value}
        case 'comment_requested':
            return {text, wantsToComment: suggestion.value === 'Yes'}
        case 'comment_required':
            return {text, value: suggestion.value}
        default:
            return {text}
    }
}

const buildRequestFromText = (
    text: string,
    stateType: InteractionState['type']
): {text: string; value?: string | number} => {
    switch (stateType) {
        case 'param_required':
        case 'comment_required':
            return {text, value: text}
        case 'rating_required': {
            const rating = Number.parseInt(text, 10)
            if (rating >= 1 && rating <= 5) {
                return {text, value: rating}
            }
            return {text}
        }
        default:
            return {text}
    }
}
